import { sum } from 'utils/calculator';
import { propEq, propSatisfies, propOr, filter, compose } from 'ramda';
import {
  MAP_ADD_ONS_STATION_UIC_TO_NAMES,
  ANY_BELGIAN_STATION_UIC,
  ADS_THROUGH_BRUSSELS_UIC,
  ANY_BELGIAN_STATION_SHORT_TARIFF_CODE,
  ADS_THROUGH_BRUSSELS_SHORT_TARIFF_CODE,
  OUTBOUND,
  INBOUND
} from 'constants/booking';

import { types } from 'types/passengers';

const isTariffCodeMatchingABSOrADS = (tariff) =>
  tariff?.slice(0, 3) === ANY_BELGIAN_STATION_SHORT_TARIFF_CODE || tariff?.slice(0, 3) === ADS_THROUGH_BRUSSELS_SHORT_TARIFF_CODE;

const findProductByTariffs = (pax, products, useAddOnsProduct) =>
  useAddOnsProduct
    ? products.find(
        (item) =>
          item.passengerId === pax.id &&
          (isTariffCodeMatchingABSOrADS(item?.tariffCode) || isTariffCodeMatchingABSOrADS(item?.tariffDetails?.tariffCode))
      )
    : products.find((item) => item.passengerId === pax.id);

export const getPaxDetails = (passengers, products, useAddOnsProduct = false) =>
  passengers.map((p) => {
    const product = products ? findProductByTariffs(p, products, useAddOnsProduct) : {};

    /**
     * When passenger and product both have a type, product overwrites passenger causing bugs,
     * so type has been removed from the product. It may be worth refactoring this code in the future
     * so the two are not merged together.
     */
    const productWithoutType = Object.assign({}, product);
    delete productWithoutType.type;

    return {
      ...p,
      ...productWithoutType
    };
  });

const getTariffCode = (tariffDetails) => tariffDetails && tariffDetails.tariffCode;

export const getPaxSummary = (passengers) =>
  passengers.reduce((obj, { tariffCode, tariffDetails, type, price, disabilityType, loyaltyPoints, bucketCode }) => {
    const id = `${tariffCode || getTariffCode(tariffDetails)}-${type}-${price}`;
    const summary = obj[id];

    return {
      ...obj,
      [id]: {
        disabilityType,
        type,
        count: summary !== undefined ? sum([summary.count, 1]) : 1,
        price,
        loyaltyPoints,
        bucketCode
      }
    };
  }, {});

export const isABSOrADSFirstLeg = (direction, originUIC, destinationUIC) =>
  (direction === OUTBOUND && [ANY_BELGIAN_STATION_UIC, ADS_THROUGH_BRUSSELS_UIC].includes(originUIC)) ||
  (direction === INBOUND && [ANY_BELGIAN_STATION_UIC, ADS_THROUGH_BRUSSELS_UIC].includes(destinationUIC));

export const getAddOnsStationNames = (direction, originUIC, destinationUIC, shouldDisplayAddOnsLegsFirst) => {
  if (shouldDisplayAddOnsLegsFirst) {
    return direction === OUTBOUND ? MAP_ADD_ONS_STATION_UIC_TO_NAMES[originUIC] : MAP_ADD_ONS_STATION_UIC_TO_NAMES[destinationUIC];
  }

  return direction === OUTBOUND ? MAP_ADD_ONS_STATION_UIC_TO_NAMES[destinationUIC] : MAP_ADD_ONS_STATION_UIC_TO_NAMES[originUIC];
};

const getAllFaresAddOns = propEq('isAFareAddOn', true);
const isAbsOrADS = compose(
  propSatisfies((tariffCode) => isTariffCodeMatchingABSOrADS(tariffCode), 'tariffCode'),
  propOr({}, 'tariffDetails')
);
export const filterFaresAddOns = (items) => compose(filter(getAllFaresAddOns), filter(isAbsOrADS))(items);

export const overrideCompanionFaresForABSADSAccessibleTravels = (paxDetails, isABSOrADS) => {
  if (isABSOrADS) {
    return !!paxDetails?.find(
      (pax) =>
        [types.WHEELCHAIR, types.COMPANION, types.NO_WHEELCHAIR_COMPANION].includes(pax?.disabilityType) &&
        pax?.type === types.ADULT &&
        paxDetails?.length > 1
    );
  }

  return false;
};

export const eurostarAndAddonLegFaresMatches = (bookingItems, isABSOrADSJourney) => {
  if (isABSOrADSJourney) {
    return !!bookingItems?.find((bi) => isTariffCodeMatchingABSOrADS(bi?.tariffCode));
  }

  return true;
};

export const hasAllLegSelectedBasedOnJourneyType = ({ isReturnJourney = false, outbound = [], inbound = [] }) => {
  const hasOneLegSelectedForAOneWayJourney = !isReturnJourney && (outbound.length > 0 || inbound.length > 0);
  const hasTwoLegsSelectedForAReturnJourney = isReturnJourney && outbound.length > 0 && inbound.length > 0;

  return hasOneLegSelectedForAOneWayJourney || hasTwoLegsSelectedForAReturnJourney;
};
